<template>
  <div
    :class="bem({
      [`theme-${promoBannerData.globalBanner.themeOverride}`]: promoBannerData.globalBanner.themeOverride !== 'disabled',
    })"
  >
    <div :class="bem('link-wrapper')">
      <VisionLink :to="'/support'" :class="bem('link')" @click="handleSupportClick">
        Help & Support
      </VisionLink>
    </div>
    <SpecialPromoBanner v-if="promoStore.banner.message" />
    <StripBanner
      v-else-if="bannerItemsToDisplay.length"
      :banner-items="bannerItemsToDisplay"
      :theme-override="promoBannerData.globalBanner.themeOverride"
    />
    <div :class="bem('link-wrapper')">
        <VisionButton
          :outline="true"
          size="x-small"
          background-colour="black"
          display="flex"
          :class="bem('link')"
          button-style="plain"
          @click="showCurrencyMenu"
          @mouseover="countryChevronColour = 'white'"
          @mouseleave="countryChevronColour = 'black'"
        >
          <template #content>
            <span :class="bem('country-button-text')">
              {{ $i18n.localeProperties.value.countryCode }}
              /
              {{ $i18n.localeProperties.value.currency }}
              <span :class="bem('country-button-chevron')">
                <CountryFlag :country="$i18n.localeProperties.value.countryCode"/>
              </span>
            </span>
          </template>
        </VisionButton>
    </div>
  </div>
</template>

<script setup>
import bannerMessageQuery from '~/gql/globalBanner.gql'
const promoStore = usePromoStore()

const { $sentry, $track, $i18n } = useNuxtApp()

const bem = useBEM('PromoBanner')

const { data: promoBannerData, error } = await useDatoQuery({
  query: bannerMessageQuery,
  prefetchedRequest: 'globalBanner',
})

if (error.value) {
  $sentry.captureException(error)
}

const bannerItemsToDisplay = computed(() => {
  return messagesToDisplay.value.map((item) => {
    return {
      id: item.id,
      text: parseMessage(item.bannerMessage),
      mobileLine1: parseMessage(item.mobileBannerMessageLine1),
      mobileLine2: parseMessage(item.mobileBannerMessageLine2),
      linkedContent: item.linkedContent,
      externalLink: item.externalLink,
      link: item.link,
    }
  })
})

const bannerMsgsFromResponse = computed(() => {
  return promoBannerData.value?.globalBanner?.globalBannerMessages || []
})

const enabledMessages = computed(() => {
  return bannerMsgsFromResponse.value.filter((bannerMsg) => bannerMsg.enabled)
})

const activeMessages = computed(() => {
  return enabledMessages.value.filter((bannerMsg) => {
    if (bannerMsg.hasGlobalPromo && !bannerMsg.globalPromo) return false

    if (!bannerMsg.activeFrom && !bannerMsg.activeUntil) return true

    const currentTimestamp = Date.parse(new Date())

    if (bannerMsg.activeFrom && !bannerMsg.activeUntil) {
      return Date.parse(bannerMsg.activeFrom) < currentTimestamp
    } else if (!bannerMsg.activeFrom && bannerMsg.activeUntil) {
      return Date.parse(bannerMsg.activeUntil) > currentTimestamp
    } else if (bannerMsg.activeFrom && bannerMsg.activeUntil) {
      return (
        Date.parse(bannerMsg.activeFrom) < currentTimestamp &&
        Date.parse(bannerMsg.activeUntil) > currentTimestamp
      )
    }

    return true
  })
})

const messagesToDisplay = computed(() => {
  return activeMessages.value.filter((enabledMsg) =>
    enabledMsg.globalPromo ? enabledMsg.globalPromo._status !== 'draft' : true
  )
})

function parseMessage(message) {
  // Support **bold**, __italic__ and ~~underline~~
  const bold = /(\*{2})(.*?)(\*{2})/g
  const italic = /(_{2})(.*?)(_{2})/g
  const underline = /(~{2})(.*?)(~{2})/g

  let parsedMessage = message
  parsedMessage = parsedMessage.replace(bold, '<b>$2</b>')
  parsedMessage = parsedMessage.replace(italic, '<i>$2</i>')
  parsedMessage = parsedMessage.replace(underline, '<u>$2</u>')

  return parsedMessage
}

function showCurrencyMenu() {
  const sidebarStore = useSidebarStore()

  sidebarStore.toggleCountrySelector()
  $track.navigation.showCurrencyMenu({
    location: 'header',
    currentCurrency: $i18n.localeProperties.currency,
  })
}

async function handleSupportClick () {
  await $track.navigation.promoBarLeftLinkClicked()
}
</script>

<style lang="postcss" scoped>
.PromoBanner {
  @apply flex px-4 bg-black w-full text-white text-tiny overflow-hidden justify-center relative z-[12] mdMax:h-6;

  @screen lg {
    padding-left: var(--outer-gutter);
    padding-right: var(--outer-gutter);
  }

  &--theme-dark {
    @apply bg-gradient;

    background: linear-gradient(135deg, rgba(54,54,54,1) 0%, rgb(41 41 41) 38%, rgba(0,0,0,1) 79%, rgba(31,67,92,1) 100%);
    background-size: 200vw 200vw;
    animation: gradient 12s ease infinite alternate;
  }

  &--theme-gold {
    @apply bg-gradient;

    background-image: var(--gradient-gold);
    background-size: 200vw 200vw;
    animation: gradient 12s ease infinite alternate;
  }

  &--theme-feb-snow-promo-24 {
    @apply bg-gradient;

    background-image: var(--gradient-feb-snow-promo-24);
    background-color: #333333;
    background-size: 200vw 200vw;
    animation: gradient 12s ease infinite alternate;
  }

  &--theme-promo {
    @apply bg-gradient;

    --promo-background-gradient: linear-gradient(115deg, var(--promo-bg) 0%, var(--promo-mid-bg) 50%, var(--promo-bg-tint) 100%);
    background-image: var(--promo-background-gradient);
    background-size: 200vw 200vw;
    animation: gradient 12s ease infinite alternate;
    color: var(--promo-shine);
  }
}

.PromoBanner__link-wrapper {
  @apply hidden md:flex
    border-r border-grey-600;

    &:last-child {
      @apply border-r-0 border-l;

      .PromoBanner__link {
        @apply pr-0 pl-10;
      }
    }
}

.PromoBanner__link {
  @apply text-white whitespace-nowrap self-center
    pr-10
    py-0.5
    text-xxs
    font-fragment-mono;
}

.PromoBanner__country-button-text {
  @apply flex items-center gap-x-2 text-white;
}
.PromoBanner__country-button-chevron {
  @apply flex items-center w-5;
}
</style>